import { logger } from "@/utils/logger";
import type { ServerAuthSession } from "@/types/auth";

/**
 * Session management utilities.
 *
 * With Supabase SSR and HTTP-only cookies:
 * - Session is managed server-side via HTTP-only cookies
 * - Client-side session operations are not available
 * - Use API routes for session operations
 * - Middleware automatically refreshes sessions
 */
export const sessionManager = {
  /**
   * Get current session from server.
   * Note: This now uses the API route instead of client-side calls.
   * For server components, use createClient() from @/utils/supabase/server directly.
   */
  getSession: async (): Promise<ServerAuthSession | null> => {
    try {
      const response = await fetch("/api/auth/session", {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        logger.debug("Failed to get session", {
          component: "SessionManager",
          status: response.status,
        });
        return null;
      }

      const data = await response.json();
      return data.session || null;
    } catch (error) {
      logger.warn("Session retrieval error", {
        component: "SessionManager",
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  },

  /**
   * Refresh the current session.
   * Note: Session refresh is handled automatically by middleware.
   * This method is kept for compatibility but does nothing.
   */
  refreshSession: async (): Promise<ServerAuthSession | null> => {
    logger.debug("Session refresh requested - handled by middleware", {
      component: "SessionManager",
    });
    // Session refresh is handled automatically by middleware
    // Return current session if needed
    return sessionManager.getSession();
  },

  /**
   * Sign out the current user.
   * Redirects to the signout API route which handles server-side signout.
   * Note: This must be called from client-side code.
   */
  signOut: async (): Promise<void> => {
    try {
      // Check if we're in a browser environment
      if (typeof window !== "undefined") {
        // Redirect to signout API route which handles server-side signout
        window.location.href = "/api/auth/signout";
      } else {
        logger.warn(
          "signOut called from server-side - use API route directly",
          {
            component: "SessionManager",
          }
        );
      }
    } catch (error) {
      logger.warn("Sign out error", {
        component: "SessionManager",
        error: error instanceof Error ? error.message : String(error),
      });
    }
  },
};
